/**
 * Shared Socket.IO client singleton for the POS app.
 *
 * Lifecycle is driven by `use-realtime-notifications.ts` (mounted once at the
 * app shell). Module load alone never opens a connection — that keeps SSR and
 * test environments quiet.
 *
 * Auth: HttpOnly cookies. The browser sends `access_token` automatically when
 * `withCredentials: true`. The backend `EventsGateway.extractToken` reads it
 * from `handshake.headers.cookie`. There is **no** identify handshake — the
 * gateway decodes the JWT at connect time and joins the appropriate
 * tenant/role/location rooms based on the token payload.
 */

import { io, type Socket } from 'socket.io-client';

export type SocketStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

type StatusListener = (status: SocketStatus) => void;

let socket: Socket | null = null;
let status: SocketStatus = 'idle';
const statusListeners = new Set<StatusListener>();

type EventHandler = (...args: unknown[]) => void;
// Every live onSocketEvent subscription, by event. connectSocket binds them to
// each new socket, so a subscription made before the socket existed, or before
// a logout/login rebuilt it, still hears its events (QA GP2-002).
const eventHandlers = new Map<string, Set<EventHandler>>();

function setStatus(next: SocketStatus): void {
  if (status === next) return;
  status = next;
  for (const fn of statusListeners) fn(status);
}

/**
 * Subscribe to status changes. Returns an unsubscribe function. Always emits
 * the current status synchronously on subscribe so consumers don't need to
 * race the first event.
 */
export function subscribeSocketStatus(fn: StatusListener): () => void {
  statusListeners.add(fn);
  fn(status);
  return () => {
    statusListeners.delete(fn);
  };
}

export function getSocketStatus(): SocketStatus {
  return status;
}

/**
 * Idempotent: returns the existing socket if already connected/connecting.
 * Re-binds the lifecycle event listeners on every call so React StrictMode
 * double-mounts don't leak handlers.
 */
export function connectSocket(): Socket {
  if (socket) return socket;

  setStatus('connecting');
  const next = io(window.location.origin, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    autoConnect: true,
  });

  next.on('connect', () => setStatus('connected'));
  next.on('disconnect', () => setStatus('disconnected'));
  next.io.on('reconnect_attempt', () => setStatus('reconnecting'));
  next.io.on('reconnect', () => setStatus('connected'));
  next.io.on('reconnect_failed', () => setStatus('disconnected'));
  for (const [event, handlers] of eventHandlers) {
    for (const handler of handlers) next.on(event, handler);
  }

  socket = next;
  return next;
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Subscribe to a server event. Returns an unsubscribe. The subscription lasts
 * until unsubscribed: it attaches to the current socket if one exists and to
 * every socket `connectSocket()` builds later.
 */
export function onSocketEvent<T = unknown>(
  event: string,
  handler: (payload: T) => void,
): () => void {
  const wrapped = ((payload: T) => handler(payload)) as EventHandler;
  const handlers = eventHandlers.get(event) ?? new Set<EventHandler>();
  eventHandlers.set(event, handlers);
  handlers.add(wrapped);
  socket?.on(event, wrapped);
  return () => {
    handlers.delete(wrapped);
    socket?.off(event, wrapped);
  };
}

/**
 * Tear down the socket and clear the cached ref so the next connectSocket()
 * call rebuilds from scratch. Safe to call when no socket exists.
 */
export function disconnectSocket(): void {
  if (!socket) {
    if (status !== 'idle') setStatus('idle');
    return;
  }
  socket.removeAllListeners();
  socket.io.removeAllListeners();
  socket.disconnect();
  socket = null;
  setStatus('idle');
}
